/** Mock of the 0112 attribute-def seed (SPEC §1.2). The real list comes from hh_mkt_attribute_defs. */
import type { AttributeDef, ListingKind } from '../types';

const AIRCRAFT: ListingKind[] = ['helicopter', 'airplane', 'gyroplane'];
const PARTSY: ListingKind[] = ['engine', 'part', 'avionics', 'accessory'];

let order = 0;
function d(
  key: string, label: string, data_type: AttributeDef['data_type'], section: AttributeDef['section'],
  opts: Partial<AttributeDef> = {},
): AttributeDef {
  order += 10;
  return {
    key, label, data_type, section,
    unit: null, enum_values: null, item_schema: null, applies_to: AIRCRAFT,
    filterable: false, sortable: false, display_order: order, synonyms: [],
    ...opts,
  };
}

const COND_1_10 = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

export const ATTRIBUTE_DEFS: AttributeDef[] = [
  // overview
  d('configuration', 'Configuration', 'enum', 'overview', {
    enum_values: ['utility', 'corporate/VIP', 'EMS', 'tour', 'law enforcement', 'training', 'ag', 'offshore', 'firefighting', 'other'],
    filterable: true,
  }),
  d('seats', 'Seats', 'integer', 'overview', { filterable: true, synonyms: ['pax', 'passengers'] }),
  d('registration_country', 'Registration country', 'text', 'overview', { synonyms: ['country of registry'] }),
  d('location_airport', 'Based at (ICAO)', 'text', 'overview', { synonyms: ['base', 'home airport', 'location'] }),
  d('ifr_certified', 'IFR certified', 'boolean', 'overview', { filterable: true, synonyms: ['ifr'] }),
  d('export_ready', 'Export ready', 'boolean', 'overview'),
  // airframe
  d('airframe_total_hours', 'Airframe total time', 'hours', 'airframe', {
    unit: 'hours', filterable: true, sortable: true, synonyms: ['TTAF', 'TT', 'total time', 'airframe hours', 'TTSN'],
  }),
  d('airframe_total_cycles', 'Airframe cycles', 'integer', 'airframe', { unit: 'cycles', synonyms: ['cycles'] }),
  d('landings', 'Landings', 'integer', 'airframe', { synonyms: ['ldgs'] }),
  d('airframe_program', 'Airframe program', 'text', 'airframe', { synonyms: ['airframe maintenance program'] }),
  d('useful_load_lbs', 'Useful load', 'number', 'airframe', { unit: 'lbs', synonyms: ['useful load'] }),
  d('max_gross_weight_lbs', 'Max gross weight', 'number', 'airframe', { unit: 'lbs', synonyms: ['MGW', 'MTOW'] }),
  d('cargo_hook', 'Cargo hook', 'boolean', 'airframe', { synonyms: ['hook'] }),
  d('float_kit', 'Float kit', 'boolean', 'airframe', { synonyms: ['floats', 'emergency floats'] }),
  d('wire_strike_kit', 'Wire strike kit', 'boolean', 'airframe', { synonyms: ['WSPS'] }),
  // engines
  d('engines', 'Engines', 'object_list', 'engines', {
    item_schema: {
      fields: [
        { key: 'position', label: 'Position', data_type: 'enum', enum_values: ['single', 'left', 'right', 'center'] },
        { key: 'make', label: 'Make', data_type: 'text' },
        { key: 'model', label: 'Model', data_type: 'text' },
        { key: 'serial', label: 'Serial', data_type: 'text' },
        { key: 'tsn_hours', label: 'TSN', data_type: 'hours', unit: 'hours' },
        { key: 'tso_hours', label: 'TSO', data_type: 'hours', unit: 'hours' },
        { key: 'tsn_cycles', label: 'Cycles', data_type: 'integer', unit: 'cycles' },
        { key: 'tbo_hours', label: 'TBO', data_type: 'hours', unit: 'hours' },
        { key: 'hot_section_hours_since', label: 'Since hot section', data_type: 'hours', unit: 'hours' },
        { key: 'overhaul_shop', label: 'Overhaul shop', data_type: 'text' },
      ],
    },
    synonyms: ['engine', 'powerplant'],
  }),
  d('engine_program', 'Engine program', 'enum', 'engines', {
    enum_values: ['none', 'PowerAdvantage', 'MSP', 'ESP', 'other'], synonyms: ['engine program', 'PAP'],
  }),
  // rotors
  d('main_rotor_blades_hours_remaining', 'Main rotor blades remaining', 'hours', 'rotors', { unit: 'hours', synonyms: ['MR blades'] }),
  d('tail_rotor_blades_hours_remaining', 'Tail rotor blades remaining', 'hours', 'rotors', { unit: 'hours', synonyms: ['TR blades'] }),
  d('life_limited_parts', 'Life-limited parts', 'object_list', 'rotors', {
    item_schema: {
      fields: [
        { key: 'part', label: 'Part', data_type: 'text' },
        { key: 'serial', label: 'Serial', data_type: 'text' },
        { key: 'hours_remaining', label: 'Hours left', data_type: 'hours', unit: 'hours' },
        { key: 'cycles_remaining', label: 'Cycles left', data_type: 'integer', unit: 'cycles' },
        { key: 'calendar_due', label: 'Calendar due', data_type: 'date' },
      ],
    },
    synonyms: ['LLP', 'LLPs', 'components'],
  }),
  // avionics
  d('avionics', 'Avionics', 'list', 'avionics', { synonyms: ['panel', 'avionics suite'] }),
  d('autopilot', 'Autopilot', 'text', 'avionics', { synonyms: ['AP', 'SAS'] }),
  d('night_vision', 'NVG compatible', 'boolean', 'avionics', { synonyms: ['NVG', 'NVIS'] }),
  // interior / exterior
  d('interior_condition', 'Interior condition', 'enum', 'interior', { enum_values: COND_1_10 }),
  d('interior_year', 'Interior year', 'integer', 'interior'),
  d('exterior_condition', 'Exterior condition', 'enum', 'exterior', { enum_values: COND_1_10 }),
  d('paint_year', 'Paint year', 'integer', 'exterior', { synonyms: ['paint'] }),
  // inspections
  d('last_annual_date', 'Last annual', 'date', 'inspections', { synonyms: ['annual'] }),
  d('next_inspection_due', 'Next inspection due', 'text', 'inspections'),
  // history
  d('damage_history', 'Damage history', 'boolean', 'history', { filterable: true, synonyms: ['NDH', 'accident'] }),
  d('damage_history_notes', 'Damage history notes', 'text', 'history'),
  d('logs_complete', 'Logs complete', 'boolean', 'history', { synonyms: ['complete logs', 'logbooks'] }),
  // parts / engines / avionics units
  d('part_number', 'Part number', 'text', 'part', { applies_to: PARTSY, synonyms: ['P/N', 'PN'] }),
  d('serial_number_part', 'Serial number', 'text', 'part', { applies_to: PARTSY, synonyms: ['S/N', 'SN'] }),
  d('condition_tag', 'Condition tag', 'enum', 'part', { applies_to: PARTSY, enum_values: ['NE', 'NS', 'OH', 'SV', 'AR', 'RP'], filterable: true }),
  d('trace', 'Trace', 'text', 'part', { applies_to: PARTSY, synonyms: ['traceability'] }),
  d('quantity', 'Quantity', 'integer', 'part', { applies_to: PARTSY, synonyms: ['qty'] }),
  d('faa_8130', 'FAA 8130-3', 'boolean', 'documents', { applies_to: PARTSY, synonyms: ['8130'] }),
  d('easa_form1', 'EASA Form 1', 'boolean', 'documents', { applies_to: PARTSY, synonyms: ['form 1'] }),
  d('tsn_hours', 'Time since new', 'hours', 'part', { applies_to: ['engine'], unit: 'hours', synonyms: ['TSN'] }),
  d('tso_hours', 'Time since overhaul', 'hours', 'part', { applies_to: ['engine'], unit: 'hours', synonyms: ['TSO', 'SMOH'] }),
];

export function defsFor(kind: ListingKind): AttributeDef[] {
  return ATTRIBUTE_DEFS.filter((a) => a.applies_to.includes(kind));
}
